/**
 * Archetype系统
 *
 * 根据实体的组件组合将实体分组到不同的原型中，提供高效的查询性能。
 */
export class ArchetypeSystem {
    constructor() {
        /** 所有原型的映射表 */
        this._archetypes = new Map();
        /** 实体到原型的映射 */
        this._entityToArchetype = new Map();
        /** 组件类型到原型的映射 */
        this._componentToArchetypes = new Map();
        /** 查询缓存 */
        this._queryCache = new Map();
        this._cacheTimeout = 5000;
        this._maxCacheSize = 100;
    }
    /**
     * 添加实体到原型系统
     */
    addEntity(entity) {
        const componentTypes = this.getEntityComponentTypes(entity);
        const archetypeId = this.generateArchetypeId(componentTypes);
        let archetype = this._archetypes.get(archetypeId);
        if (!archetype) {
            archetype = this.createArchetype(componentTypes);
        }
        archetype.entities.push(entity);
        archetype.updatedAt = Date.now();
        this._entityToArchetype.set(entity, archetype);
        this.updateComponentIndexes(archetype, componentTypes, true);
        this.invalidateQueryCache();
    }
    /**
     * 从原型系统中移除实体
     */
    removeEntity(entity) {
        const archetype = this._entityToArchetype.get(entity);
        if (!archetype)
            return;
        const index = archetype.entities.indexOf(entity);
        if (index !== -1) {
            archetype.entities.splice(index, 1);
            archetype.updatedAt = Date.now();
        }
        this._entityToArchetype.delete(entity);
        this.invalidateQueryCache();
    }
    /**
     * 查询包含指定组件组合的原型
     */
    queryArchetypes(componentTypes, operation = 'AND') {
        const startTime = performance.now();
        const cacheKey = `${operation}:${componentTypes.map(t => t.name).sort().join(',')}`;
        // 检查缓存
        const cached = this._queryCache.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp < this._cacheTimeout)) {
            return {
                ...cached.result,
                executionTime: performance.now() - startTime,
                fromCache: true
            };
        }
        const matchingArchetypes = [];
        let totalEntities = 0;
        if (operation === 'AND') {
            for (const archetype of this._archetypes.values()) {
                if (this.archetypeContainsAllComponents(archetype, componentTypes)) {
                    matchingArchetypes.push(archetype);
                    totalEntities += archetype.entities.length;
                }
            }
        }
        else {
            const foundArchetypes = new Set();
            for (const componentType of componentTypes) {
                const archetypes = this._componentToArchetypes.get(componentType);
                if (archetypes) {
                    for (const archetype of archetypes) {
                        foundArchetypes.add(archetype);
                    }
                }
            }
            for (const archetype of foundArchetypes) {
                matchingArchetypes.push(archetype);
                totalEntities += archetype.entities.length;
            }
        }
        const result = {
            archetypes: matchingArchetypes,
            totalEntities,
            executionTime: performance.now() - startTime,
            fromCache: false
        };
        // 缓存结果
        this._queryCache.set(cacheKey, {
            result,
            timestamp: Date.now()
        });
        return result;
    }
    /**
     * 获取实体所属的原型
     */
    getEntityArchetype(entity) {
        return this._entityToArchetype.get(entity);
    }
    /**
     * 获取所有原型
     */
    getAllArchetypes() {
        return Array.from(this._archetypes.values());
    }
    /**
     * 清空所有数据
     */
    clear() {
        this._archetypes.clear();
        this._entityToArchetype.clear();
        this._componentToArchetypes.clear();
        this._queryCache.clear();
    }
    /**
     * 获取实体的组件类型列表
     */
    getEntityComponentTypes(entity) {
        return entity.components.map(component => component.constructor);
    }
    /**
     * 生成原型ID
     */
    generateArchetypeId(componentTypes) {
        return componentTypes
            .map(type => type.name)
            .sort()
            .join('|');
    }
    /**
     * 创建新原型
     */
    createArchetype(componentTypes) {
        const id = this.generateArchetypeId(componentTypes);
        const archetype = {
            id,
            componentTypes: [...componentTypes],
            entities: [],
            createdAt: Date.now(),
            updatedAt: Date.now()
        };
        this._archetypes.set(id, archetype);
        return archetype;
    }
    /**
     * 检查原型是否包含所有指定组件
     */
    archetypeContainsAllComponents(archetype, componentTypes) {
        for (const componentType of componentTypes) {
            if (!archetype.componentTypes.includes(componentType)) {
                return false;
            }
        }
        return true;
    }
    /**
     * 更新组件索引
     */
    updateComponentIndexes(archetype, componentTypes, add) {
        for (const componentType of componentTypes) {
            let archetypes = this._componentToArchetypes.get(componentType);
            if (!archetypes) {
                archetypes = new Set();
                this._componentToArchetypes.set(componentType, archetypes);
            }
            if (add) {
                archetypes.add(archetype);
            }
            else {
                archetypes.delete(archetype);
                if (archetypes.size === 0) {
                    this._componentToArchetypes.delete(componentType);
                }
            }
        }
    }
    /**
     * 使查询缓存失效
     */
    invalidateQueryCache() {
        this._queryCache.clear();
    }
}
//# sourceMappingURL=ArchetypeSystem.js.map